import { useState } from 'react';
import { alpha, theme } from '../../theme.js';
import { useAuth } from '../../context/AuthContext.js';
import {
  useSchedules, useCreateSchedule, useGenerateRuns, useUpdateRotation,
  useAssets, useTeams,
} from '../../lib/queries.js';
import type { Rotation } from '../../lib/queries.js';
import { Icon } from '../../components/Icon.js';
import { NavLink } from 'react-router-dom';
import type { Schedule, Cadence, Asset, Team } from '@keetcheck/shared';

const CADENCE_LABEL: Record<Cadence, string> = {
  weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly',
};
const PERIOD_WORD: Record<Cadence, string> = { weekly: 'week', monthly: 'month', quarterly: 'quarter' };

// Shapes of the aggregated rows the schedules endpoint returns.
interface SchedAsset { assetId: string; assetName: string; slot: number; sortOrder: number; active: boolean }
interface SchedTeam { teamId: string; teamName: string; sortOrder: number; disabled: boolean; active: boolean }

export function SchedulesPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';

  const { data: schedules = [], isLoading } = useSchedules(orgId);
  const { data: assets = [] } = useAssets(orgId);
  const { data: teams = [] } = useTeams(orgId);
  const createMut = useCreateSchedule(orgId);

  const [showForm, setShowForm] = useState(false);
  const [name, setName]           = useState('');
  const [cadence, setCadence]     = useState<Cadence>('weekly');
  const [startDate, setStartDate] = useState('');
  const [rotation, setRotation]   = useState<Rotation>({ slots: [[]], teamIds: [] });

  const valid = !!name.trim() && !!startDate && rotationValid(rotation);

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    createMut.mutate(
      { name: name.trim(), cadence, startDate, rotation: cleanRotation(rotation) },
      {
        onSuccess: () => {
          setName(''); setStartDate(''); setRotation({ slots: [[]], teamIds: [] });
          setShowForm(false);
        },
      },
    );
  }

  return (
    <div style={{ flex: 1, background: theme.surface }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', background: theme.bg, borderBottom: `1px solid ${theme.border}`,
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: theme.textDim }}>
          {schedules.length} schedule{schedules.length !== 1 ? 's' : ''}
        </span>
        <button onClick={() => setShowForm(!showForm)} style={addBtn}>
          <Icon name="plus" size={15} /> New schedule
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} style={{
          padding: '14px 16px', background: theme.surface2, borderBottom: `1px solid ${theme.border}`,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <input required value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Schedule name (e.g. Weekly Vehicle Check)" aria-label="Schedule name" style={inputStyle} />
          <div style={{ display: 'flex', gap: 10 }}>
            <label style={{ flex: 1 }}>
              <FieldLabel>Cadence</FieldLabel>
              <select value={cadence} onChange={(e) => setCadence(e.target.value as Cadence)} style={inputStyle}>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </label>
            <label style={{ flex: 1 }}>
              <FieldLabel>Start date</FieldLabel>
              <input required type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
            </label>
          </div>

          <RotationEditor value={rotation} onChange={setRotation} assets={assets} teams={teams} cadence={cadence} />

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={() => setShowForm(false)} style={cancelBtn}>Cancel</button>
            <button type="submit" disabled={createMut.isPending || !valid} style={submitBtnStyle}>
              {createMut.isPending ? 'Creating…' : 'Create schedule'}
            </button>
          </div>
        </form>
      )}

      {isLoading && <EmptyMsg>Loading…</EmptyMsg>}
      {!isLoading && schedules.length === 0 && (
        <EmptyMsg>No schedules yet — create one to start rotating teams.</EmptyMsg>
      )}

      {schedules.map((s, i) => (
        <ScheduleCard key={s.id} schedule={s} orgId={orgId} assets={assets} teams={teams}
          last={i === schedules.length - 1} />
      ))}
    </div>
  );
}

// ── Schedule card ─────────────────────────────────────────────

function ScheduleCard({ schedule, orgId, assets, teams, last }: {
  schedule: Schedule; orgId: string; assets: Asset[]; teams: Team[]; last: boolean;
}) {
  const genMut = useGenerateRuns(orgId, schedule.id);
  const rotMut = useUpdateRotation(orgId, schedule.id);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Rotation>(() => rotationOf(schedule));

  const sAssets = (schedule.assets ?? []) as unknown as SchedAsset[];
  const sTeams = (schedule.teams ?? []) as unknown as SchedTeam[];
  const slotCount = new Set(sAssets.map((a) => a.slot)).size;
  const cur = schedule.current;

  return (
    <div style={{
      padding: '14px 18px', background: theme.surface,
      borderBottom: last ? 'none' : `1px solid ${theme.border}`, opacity: schedule.active ? 1 : 0.6,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 8, flexShrink: 0,
          background: theme.surface2, border: `1px solid ${theme.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textMuted,
        }}>
          <Icon name="calendar" size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>
            {schedule.name}{!schedule.active && ' (retired)'}
          </div>
          <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
            {CADENCE_LABEL[schedule.cadence]} · from {fmtDate(schedule.startDate)} ·{' '}
            {slotCount} slot{slotCount !== 1 ? 's' : ''} · {sTeams.filter((t) => t.active).length} teams
          </div>
        </div>
        {schedule.active && !editing && (
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button onClick={() => { setDraft(rotationOf(schedule)); setEditing(true); }} style={smallBtn}>Edit rotation</button>
            <button onClick={() => genMut.mutate()} disabled={genMut.isPending} style={smallBtn}>
              {genMut.isPending ? '…' : 'Generate runs'}
            </button>
          </div>
        )}
      </div>

      {genMut.isSuccess && (
        <Notice tone="ok">
          ✓ {genMut.data.created.length} run{genMut.data.created.length !== 1 ? 's' : ''} created
          {genMut.data.skipped.length ? ` · ${genMut.data.skipped.length} already existed` : ''}
          {genMut.data.uncoveredSlots.length ? ` · slot ${genMut.data.uncoveredSlots.join(', ')} not covered` : ''}
        </Notice>
      )}

      {editing ? (
        <div style={{ marginTop: 12, padding: 12, background: theme.surface2, borderRadius: 10 }}>
          <RotationEditor value={draft} onChange={setDraft} assets={assets} teams={teams} cadence={schedule.cadence} />
          <div style={{ fontSize: 12, color: theme.textMuted, margin: '10px 0' }}>
            Changes apply from the next runs generated; checks already created stay as they are.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={() => setEditing(false)} style={cancelBtn}>Cancel</button>
            <button type="button" disabled={!rotationValid(draft) || rotMut.isPending}
              onClick={() => rotMut.mutate(cleanRotation(draft), { onSuccess: () => setEditing(false) })}
              style={submitBtnStyle}>
              {rotMut.isPending ? 'Saving…' : 'Save rotation'}
            </button>
          </div>
        </div>
      ) : schedule.active && (
        <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 10 }}>
          Week-by-week changes (who checks what, crews, UDOs) are on the{' '}
          <NavLink to="/admin/roster" style={{ color: theme.accent, fontWeight: 700 }}>Roster</NavLink> tab.
        </div>
      )}
    </div>
  );
}

// ── Rotation editor ───────────────────────────────────────────

/**
 * Slots (groups of assets checked together) and the ordered teams that
 * rotate through them — each period every team moves to the next slot.
 */
function RotationEditor({ value, onChange, assets, teams, cadence }: {
  value: Rotation; onChange: (r: Rotation) => void; assets: Asset[]; teams: Team[]; cadence: Cadence;
}) {
  const used = new Set(value.slots.flat());
  const free = assets.filter((a) => !used.has(a.id));
  const nameOf = (id: string) => assets.find((a) => a.id === id)?.name ?? 'Retired asset';
  const team = (id: string) => teams.find((t) => t.id === id);
  const setSlots = (slots: string[][]) => onChange({ ...value, slots });
  const setTeams = (teamIds: string[]) => onChange({ ...value, teamIds });
  const slotCount = value.slots.filter((s) => s.length).length;
  const teamCount = value.teamIds.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <FieldLabel>Slots — assets checked together by one team</FieldLabel>
        {value.slots.map((slot, i) => (
          <div key={i} style={{
            border: `1px solid ${theme.border}`, borderRadius: 10, padding: 10, marginTop: 6, background: theme.surface,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <b style={{ fontSize: 13, color: theme.text, flex: 1 }}>Slot {i + 1}</b>
              <MiniBtn label="Move up" disabled={i === 0} onClick={() => setSlots(move(value.slots, i, i - 1))}>↑</MiniBtn>
              <MiniBtn label="Move down" disabled={i === value.slots.length - 1} onClick={() => setSlots(move(value.slots, i, i + 1))}>↓</MiniBtn>
              <MiniBtn label="Remove slot" disabled={value.slots.length === 1} onClick={() => setSlots(value.slots.filter((_, j) => j !== i))}>×</MiniBtn>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              {slot.map((id) => (
                <span key={id} style={chip}>
                  {nameOf(id)}
                  <button type="button" aria-label={`Remove ${nameOf(id)}`}
                    onClick={() => setSlots(value.slots.map((s, j) => (j === i ? s.filter((x) => x !== id) : s)))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, fontSize: 14 }}>×</button>
                </span>
              ))}
              {free.length > 0 && (
                <select value="" aria-label={`Add asset to slot ${i + 1}`}
                  onChange={(e) => e.target.value && setSlots(value.slots.map((s, j) => (j === i ? [...s, e.target.value] : s)))}
                  style={{ ...inputStyle, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
                  <option value="">+ Add asset…</option>
                  {free.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              )}
              {!slot.length && !free.length && <span style={{ fontSize: 12, color: theme.textMuted }}>All assets are in other slots.</span>}
            </div>
          </div>
        ))}
        <button type="button" onClick={() => setSlots([...value.slots, []])} style={{ ...smallBtn, marginTop: 8 }}>
          + Add slot
        </button>
      </div>

      <div>
        <FieldLabel>Teams — in rotation order</FieldLabel>
        {value.teamIds.map((id, i) => (
          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
            <span style={{ width: 22, fontSize: 12, color: theme.textMuted }}>{i + 1}.</span>
            <span style={{ flex: 1, fontSize: 13, color: theme.text }}>
              {team(id)?.name ?? 'Retired team'}
              {team(id)?.disabledAt && <span style={{ color: theme.textMuted }}> (disabled)</span>}
            </span>
            <MiniBtn label="Move up" disabled={i === 0} onClick={() => setTeams(move(value.teamIds, i, i - 1))}>↑</MiniBtn>
            <MiniBtn label="Move down" disabled={i === value.teamIds.length - 1} onClick={() => setTeams(move(value.teamIds, i, i + 1))}>↓</MiniBtn>
            <MiniBtn label="Remove team" onClick={() => setTeams(value.teamIds.filter((x) => x !== id))}>×</MiniBtn>
          </div>
        ))}
        {teams.some((t) => !value.teamIds.includes(t.id)) && (
          <select value="" aria-label="Add team" onChange={(e) => e.target.value && setTeams([...value.teamIds, e.target.value])}
            style={{ ...inputStyle, marginTop: 8, width: 'auto', padding: '6px 10px', fontSize: 12 }}>
            <option value="">+ Add team…</option>
            {teams.filter((t) => !value.teamIds.includes(t.id)).map((t) => (
              <option key={t.id} value={t.id}>{t.name}{t.disabledAt ? ' (disabled)' : ''}</option>
            ))}
          </select>
        )}
      </div>

      {slotCount > 0 && teamCount > 0 && (
        <div style={{ fontSize: 12, color: theme.textDim, lineHeight: 1.5, padding: '8px 10px', background: theme.bg, borderRadius: 8 }}>
          Each {PERIOD_WORD[cadence]} every team moves on to the next slot, so it takes {Math.max(slotCount, teamCount)}{' '}
          {PERIOD_WORD[cadence]}s for each team to cover every slot.
          {teamCount < slotCount && ` With ${teamCount} team${teamCount > 1 ? 's' : ''} and ${slotCount} slots, ${slotCount - teamCount} slot${slotCount - teamCount > 1 ? 's go' : ' goes'} unchecked each ${PERIOD_WORD[cadence]} (rotating).`}
          {teamCount > slotCount && ` With more teams than slots, ${teamCount - slotCount} team${teamCount - slotCount > 1 ? 's sit' : ' sits'} out each ${PERIOD_WORD[cadence]} (rotating).`}
        </div>
      )}
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────

function rotationOf(s: Schedule): Rotation {
  const assets = ((s.assets ?? []) as unknown as SchedAsset[]).slice().sort((a, b) => a.slot - b.slot || a.sortOrder - b.sortOrder);
  const slots: string[][] = [];
  for (const a of assets) (slots[a.slot] ??= []).push(a.assetId);
  const teams = ((s.teams ?? []) as unknown as SchedTeam[]).filter((t) => t.active).sort((a, b) => a.sortOrder - b.sortOrder);
  return { slots: slots.filter(Boolean).length ? slots.filter(Boolean) : [[]], teamIds: teams.map((t) => t.teamId) };
}
const cleanRotation = (r: Rotation): Rotation => ({ ...r, slots: r.slots.filter((s) => s.length) });
const rotationValid = (r: Rotation) => r.slots.some((s) => s.length) && r.teamIds.length > 0;
function move<T>(arr: T[], from: number, to: number): T[] {
  const a = arr.slice();
  const [x] = a.splice(from, 1);
  a.splice(to, 0, x!);
  return a;
}

function MiniBtn({ children, label, disabled, onClick }: {
  children: React.ReactNode; label: string; disabled?: boolean; onClick: () => void;
}) {
  return (
    <button type="button" aria-label={label} title={label} disabled={disabled} onClick={onClick} style={{
      width: 26, height: 26, borderRadius: 6, border: `1px solid ${theme.border}`, background: theme.surface2,
      color: theme.textDim, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1,
      fontFamily: theme.font, fontSize: 13, lineHeight: 1,
    }}>{children}</button>
  );
}

function Notice({ tone, children }: { tone: 'ok' | 'warn'; children: React.ReactNode }) {
  const t = theme.status[tone];
  return (
    <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 6, background: t.bg, color: t.fg, fontSize: 12, fontWeight: 600 }}>
      {children}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: theme.textDim, marginBottom: 4, letterSpacing: 0.2 }}>
      {children}
    </div>
  );
}

function EmptyMsg({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: '40px 20px', textAlign: 'center', color: theme.textMuted, fontSize: 14 }}>
      {children}
    </div>
  );
}

function fmtDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}
const fmtShort = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

const chip: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
  border: `1px solid ${theme.accent}`, background: alpha(theme.accent, 9), color: theme.accent,
};

const inputStyle: React.CSSProperties = {
  padding: '9px 12px', background: theme.surface,
  border: `1px solid ${theme.border}`, borderRadius: 10,
  fontSize: 13, color: theme.text, fontFamily: theme.font,
  outline: 'none', width: '100%', boxSizing: 'border-box',
};

const addBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '7px 14px', borderRadius: 8,
  background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const smallBtn: React.CSSProperties = {
  padding: '6px 12px', borderRadius: 8, flexShrink: 0,
  background: theme.surface2, border: `1px solid ${theme.border}`,
  color: theme.textDim, fontSize: 12, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const cancelBtn: React.CSSProperties = {
  flex: 1, padding: '10px 0', borderRadius: 10,
  background: theme.surface, border: `1px solid ${theme.border}`,
  color: theme.textDim, fontSize: 14, fontWeight: 600,
  cursor: 'pointer', fontFamily: theme.font,
};

const submitBtnStyle: React.CSSProperties = {
  flex: 2, padding: '10px 0', borderRadius: 10,
  background: theme.accent, border: 'none',
  color: theme.accentInk, fontSize: 14, fontWeight: 700,
  cursor: 'pointer', fontFamily: theme.font,
};
